import { ReactNode, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { Input } from "sr/helpers";
import { LevelBar } from "sr/helpers/ui-components/LevelBar";
import { Button } from "sr/helpers/ui-components/company/Button";
import ContentWithSpinner from "sr/layout/common/ContentWithSpiner";
import { useValidationErrorHandle } from "sr/helpers/functions/validationErrorHandle";
import { scrollToError } from "sr/helpers/functions/scrollToError";
import AutoLocation from "sr/helpers/ui-components/AutoLocation";
import { RequirementCommonHeader } from "../layout/RequirementCommonHeader";
import { LoaderFullScreen } from "sr/layout/common/LoaderFullScreen";
import { uploadFile } from "sr/utils/api/uploadFile";
import { toast } from "react-toastify";
import {
  updateRequirement,
  getRequirements,
} from "sr/utils/api/corporate/requirements";
import { useSelector } from "react-redux";
import DocumentUpload from "sr/helpers/ui-components/DocumentUpload";
import { RequirementAdvancedSchema } from "sr/utils/schemas/corporate/requirements/RequirementAdvancedSchema";

type AddressType = {
  address_line_1?: string;
  address_line_2?: string;
  country?: string;
  city?: string;
  state?: string;
  postal?: string;
  lat?: number;
  lng?: number;
};

type AdvancedValues = {
  address: AddressType | null;
  special_instructions: string;
  documents_required: string[];
  documents_upload: File[];
};

export default function RequirementAdvanced() {
  const intl = useIntl();
  const { reqId } = useParams<{ reqId: string }>();
  const navigate = useNavigate();
  const validationErrorHandle = useValidationErrorHandle();
  const [loader, setLoader] = useState<"" | "submit" | "fetch">("");
  const [docPreviews, setDocPreviews] = useState<any[]>([]);
  const [docsInput, setDocsInput] = useState("");
  const [existingData, setExistingData] = useState<any>(null);
  const [existingAttachments, setExistingAttachments] = useState<any[]>([]);
  const corporateData = useSelector((state: any) => state.corporate?.data);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const [address, setAddress] = useState<AddressType | null>(null);

  const corporateId = corporateData?.id;

  useEffect(() => {
    if (!reqId || !corporateId) {
      navigate("/corporate/requirement-post/basic");
      return;
    }

    const fetchExisting = async () => {
      setLoader("fetch");
      const res = await getRequirements({ page: 1, limit: 100 });
      if (res?.data?.length) {
        const match = res.data.find(
          (r: any) =>
            String(r.id) === String(reqId) &&
            String(r.corporate_id) === String(corporateId)
        );
        if (match) {
          setExistingData(match);
          setAddress(match.address || null);

          if (match.documents_required?.length) {
            setDocsInput(
              match.documents_required.map((d: any) => d.doc_name).join(", ")
            );
          }

          if (match.attachments?.length) {
            const previews = match.attachments.map((a: any, i: number) => ({
              name: a.name || a.url.split("/").pop(),
              url: a.url,
              isExisting: true,
              index: i,
            }));
            setDocPreviews(previews);
            setExistingAttachments(match.attachments);
          }
        } else {
          console.warn("Requirement not found for reqId:", reqId);
        }
      }
      setLoader("");
    };

    fetchExisting();
  }, [reqId, corporateId, navigate]);

  const initialValues: AdvancedValues = {
    address: address,
    special_instructions: existingData?.special_instructions || "",
    documents_required:
      existingData?.documents_required?.map((d: any) => d.doc_name) || [],
    documents_upload: [],
  };

  const { errors, submitForm, values, setFieldValue, touched } =
    useFormik<AdvancedValues>({
      enableReinitialize: true,
      initialValues,
      validationSchema: RequirementAdvancedSchema,
      validateOnChange: false,
      validateOnBlur: true,
      onSubmit: async (vals) => {
        if (!reqId) return;
        setLoader("submit");
        try {
          const uploadFiles: File[] = vals.documents_upload || [];
          const uploadedFiles: { url: string; name: string }[] = [];

          for (const file of uploadFiles) {
            const formData = new FormData();
            formData.append("task_img", file);
            const res = await uploadFile(formData);
            if (res?.success === true) {
              const key = res?.data?.files?.[0]?.key;
              if (key) {
                uploadedFiles.push({ url: key, name: file.name });
              }
            }
          }

          const payload = {
            ...existingData,
            address: {
              address_line_1: address?.address_line_1 || "",
              address_line_2: address?.address_line_2 || "",
              city: address?.city || "",
              state: address?.state || "",
              country: address?.country || "",
              postal: address?.postal || "",
              lat: address?.lat,
              lng: address?.lng,
            },
            special_instructions: vals.special_instructions?.trim(),
            documents_required: (vals.documents_required || []).map(
              (doc: string) => ({
                doc_name: doc.trim(),
              })
            ),
            attachments: [
              ...existingAttachments,
              ...uploadedFiles,
            ],
          };

          

          const res = await updateRequirement(reqId, payload);
          if (res?.success) {
            navigate(`/corporate/requirement-post/review/${reqId}`);
          }
        } catch (e) {
          toast.error(intl.formatMessage({ id: 'API.ERROR.GENERIC.TRY_AGAIN', defaultMessage: 'Something went wrong, please try again' }));
        } finally {
          setLoader("");
        }
      },
    });

  useEffect(() => {
    scrollToError(errors);
  }, [errors]);

  const errorMsg = (name: keyof AdvancedValues) =>
    ((touched as any)[name] || submitAttempted) && (errors as any)[name] ? (
      <p className="text-sm text-red-500">
        {validationErrorHandle((errors as any)[name] as ReactNode)}
      </p>
    ) : null;

  const handleDocumentSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const newDocs = [...values.documents_upload, ...files];
    setFieldValue("documents_upload", newDocs);

    const previews = files.map((file) => ({
      url: URL.createObjectURL(file),
      name: file.name,
      isExisting: false,
    }));
    setDocPreviews((prev) => [...prev, ...previews]);
  };

  const removeDocument = (index: number) => {
    const target = docPreviews[index];

    setDocPreviews((prev) => prev.filter((_, i) => i !== index));

    if (!target?.isExisting) {
      const newDocs = values.documents_upload.filter((_, i) => i !== index);
      setFieldValue("documents_upload", newDocs);
    } else {
      setExistingAttachments((prev) =>
        prev.filter((_, i) => i !== target.index)
      );
    }
  };

  return (
    <div>
      {loader === "submit" && <LoaderFullScreen />}
      <ContentWithSpinner loader={loader === "fetch"} height="h-screen">
        <div className="container mx-auto max-w-4xl py-8 px-4">
          <RequirementCommonHeader
            title={
              <FormattedMessage
                id="CORPORATE.REQUIREMENT.POST.TITLE"
                defaultMessage="Advanced Details"
              />
            }
          />
          <div className="bg-white rounded-2xl shadow p-8">
            <LevelBar progress={2} total={3} />
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setSubmitAttempted(true);
                submitForm();
              }}
              className="space-y-10 mt-8"
              noValidate
            >
              <div>
                <p className="text-base">
                  <FormattedMessage
                    id="CORPORATE.REQUIREMENT.POST.ADDRESS"
                    defaultMessage="Address"
                  />
                </p>
                <div className="mt-2">
                  <AutoLocation
                    HandleAutoLocation={(data: AddressType) => {
                      setAddress(data);
                      setFieldValue("address", data);
                    }}
                    errors={(errors as any)?.address}
                  />
                </div>
                {errorMsg("address")}
              </div>

              <Input
                label={
                  <FormattedMessage
                    id="CORPORATE.REQUIREMENT.POST.SPECIAL"
                    defaultMessage="Special Instructions"
                  />
                }
                name="special_instructions"
                value={values.special_instructions}
                onChange={(e: any) =>
                  setFieldValue("special_instructions", e.target.value)
                }
                error={errors.special_instructions}
                placeholder={intl.formatMessage({ id: 'CORPORATE.REQUIREMENT.POST.PLACEHOLDER.SPECIAL', defaultMessage: 'Optional special instructions' })}
              />
              
              <Input
                label={
                  <FormattedMessage
                    id="CORPORATE.REQUIREMENT.POST.DOCSREQ"
                    defaultMessage="Documents Required"
                  />
                }
                name="documents_required"
                value={docsInput}
                onChange={(e) => {
                  const val = e.target.value;
                  setDocsInput(val);
                  setFieldValue(
                    "documents_required",
                    val
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean)
                  );
                }}
                error={errors.documents_required}
                placeholder={intl.formatMessage({ id: 'CORPORATE.REQUIREMENT.POST.PLACEHOLDER.DOCS', defaultMessage: 'Enter required documents, separated by commas' })}
              />
              
              <DocumentUpload
                documents={docPreviews}
                onUpload={handleDocumentSelection}
                onRemove={removeDocument}
              />

              <div className="flex justify-between">
                <div />
                <Button
                  text={
                    <FormattedMessage
                      id="RECRUITER.POSTJOB.REVIEW.PREVIEW.CONFIRM.BUTTON"
                      defaultMessage="Continue"
                    />
                  }
                />
              </div>
            </form>
          </div>
        </div>
      </ContentWithSpinner>
    </div>
  );
}
